import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "../lib/api";

// Responsive Hero:
//   • Desktop (lg and above): NEW approved horizontal artwork (`hero-desktop.webp`).
//     Native resolution 1448×1086. Displayed at native max-width so the image is
//     never upscaled and never cropped. Centered horizontally. A very subtle
//     bottom fade blends into the site background.
//   • Mobile / tablet (below lg): EXISTING approved vertical hero
//     (`hero-complete.webp`) — DO NOT touch. Byte-identical to the previously
//     approved mobile Hero.
//
// Desktop-only logo overlay:
//   The desktop Hero artwork has a burnt-in circular CrLuys LIFESTYLE logo in
//   its upper-left area (~x=33, y=13, size≈175×186 within the 1448×1086 canvas).
//   We overlay the NEW official logo (`/crluys-icon.webp`, which includes the
//   small green sparkle) precisely on top of that region using percentage-based
//   positioning so it scales together with the underlying Hero image at every
//   desktop viewport. The Hero image file bytes are NOT modified.
//   Mobile is untouched — no overlay applied there.
const HERO_DESKTOP = "/lifestyle/hero-desktop.webp?v=final2";
const HERO_MOBILE = HERO_DESKTOP; // Mobile serves the SAME approved desktop asset — single source of truth
const LOGO_OVERLAY = "/crluys-icon.webp?v=final2";
const WORDMARK_OVERLAY = "/lifestyle/wordmark-overlay.webp?v=final2";

// Baked-in circular logo box within hero-desktop.webp (measured with strict gold
// detection): center at approximately x=87, y=67 with radius ≈ 54 →
// box left=31, top=11, size=113×113. As percentages of the 1448×1086 image:
const OVERLAY_STYLE = {
    left: `${(31 / 1448) * 100}%`,
    top: `${(11 / 1086) * 100}%`,
    width: `${(113 / 1448) * 100}%`,
    height: `${(113 / 1086) * 100}%`,
    // Radial mask: keep the center 74% fully opaque, fade the outer edge to
    // transparent so the logo's black background dissolves cleanly into the
    // surrounding night sky without any visible rectangular seam.
    WebkitMaskImage:
        "radial-gradient(circle at 50% 50%, #000 74%, rgba(0,0,0,0.6) 88%, transparent 100%)",
    maskImage:
        "radial-gradient(circle at 50% 50%, #000 74%, rgba(0,0,0,0.6) 88%, transparent 100%)",
};

// Baked-in "CrLuys LIFESTYLE" wordmark inside hero-desktop.webp (measured
// precisely from a hi-res crop): left=535, top=102, w=380, h=183.
// Immediately below (from y≈325) is the "Elevate Your Health / Life"
// headline — the backdrop MUST NOT reach into that region.
//
// The NEW logo asset has aspect ratio 1122/767 ≈ 1.463, so we use a wrapping
// box of 380 × 260 (aspect 1.462 — matches the source exactly, no letterboxing,
// no distortion) and position it so its vertical centre sits on the same
// centre as the baked-in wordmark. This means the new logo occupies the same
// horizontal footprint (width 380 = same as baked-in) but is a little taller,
// filling a small amount of the empty sky above and just kissing the sky
// below the wordmark. It never touches the headline (bottom edge at y=298).
const WORDMARK_BOX = {
    left: `${(535 / 1448) * 100}%`,
    top: `${(60 / 1086) * 100}%`,
    width: `${(380 / 1448) * 100}%`,
    height: `${(260 / 1086) * 100}%`,
};
// Backdrop covers exactly the baked-in wordmark footprint (with a small
// margin) so the underlying gold letters are fully hidden. Beyond that
// rectangle the fade rapidly reaches full transparency so the surrounding
// night sky, palms and clouds are untouched.
const WORDMARK_BACKDROP_STYLE = {
    left: `${((535 - 24) / 1448) * 100}%`,
    top: `${((102 - 14) / 1086) * 100}%`,
    width: `${((380 + 48) / 1448) * 100}%`,
    height: `${((183 + 28) / 1086) * 100}%`,
    // Very subtle blur+dim behind the new logo, sampled to match the surrounding
    // night sky. Just enough opacity in the centre to muddy the baked-in gold
    // shapes so they don't ghost through the transparent new logo. Fades to
    // fully invisible before touching the outer sky/palm silhouettes.
    background:
        "radial-gradient(ellipse 60% 55% at 50% 50%, rgba(4,20,34,0.85) 40%, rgba(4,20,34,0.5) 70%, rgba(4,20,34,0.15) 88%, rgba(4,20,34,0) 100%)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
};
const WORDMARK_STYLE = {
    ...WORDMARK_BOX,
    objectFit: "contain",
};

// Tap targets over the category labels painted into the hero artwork (x ranges in the 1448×1086 image).
const PAINTED_NAV_X = [[243, 390], [401, 567], [578, 747], [758, 903], [914, 1078], [1088, 1175]];
const PAINTED_NAV = [
    ...CATEGORIES.map((c) => ({ name: c.short, to: `/category/${c.slug}` })),
    { name: "Contact", to: "/contact" },
].map((n, i) => ({
    ...n,
    testid: `hero-nav-${n.name.replace(/&/g, "and").replace(/\s+/g, "-").toLowerCase()}`,
    style: {
        left: `${(PAINTED_NAV_X[i][0] / 1448) * 100}%`,
        width: `${((PAINTED_NAV_X[i][1] - PAINTED_NAV_X[i][0]) / 1448) * 100}%`,
        top: `${(26 / 1086) * 100}%`,
        height: `${(50 / 1086) * 100}%`,
        WebkitTapHighlightColor: "transparent",
    },
}));

function PaintedNavLinks() {
    return PAINTED_NAV.map((n) => (
        <Link
            key={n.to}
            to={n.to}
            aria-label={n.name}
            data-testid={n.testid}
            className="absolute z-10 block cursor-pointer"
            style={n.style}
        />
    ));
}

export default function Hero() {
    return (
        <section
            data-testid="hero-section"
            className="relative w-full bg-ink pt-28 lg:overflow-hidden"
        >
            {/* Ambient background layer (all viewports) */}
            <div
                aria-hidden="true"
                className="hidden lg:block absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: `url(${HERO_DESKTOP})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(64px) brightness(0.28) saturate(1.1)",
                    transform: "scale(1.2)",
                    transformOrigin: "center",
                }}
            />
            {/* Warm charcoal wash */}
            <div
                aria-hidden="true"
                className="hidden lg:block absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(10,9,8,0) 0%, rgba(10,9,8,0.55) 65%, rgba(10,9,8,0.92) 100%)",
                }}
            />

            {/* HERO IMAGE — single source of truth for both desktop and mobile.
                Uses the approved desktop artwork (`hero-desktop.webp`) as the
                base and applies the same runtime overlays (small circular logo
                top-left + new CrLuys LIFESTYLE wordmark centre) on every
                viewport. Overlays scale with the image via percentage
                positioning, so mobile is a responsive miniature of the exact
                same approved design. */}
            <div className="relative w-full lg:max-w-[1448px] mx-auto">
                <img
                    src={HERO_DESKTOP}
                    alt="CrLuys Lifestyle — Elevate Your Health. Elevate Your Life. Curated resources for those who choose better health, financial freedom, fitness, mindset and intentional living."
                    className="hero-edge-fade relative block w-full h-auto select-none"
                    draggable={false}
                    fetchPriority="high"
                    decoding="async"
                    data-testid="hero-desktop-image"
                />
                <img
                    src={LOGO_OVERLAY}
                    alt="CrLuys Lifestyle"
                    className="absolute pointer-events-none select-none"
                    style={{
                        ...OVERLAY_STYLE,
                        objectFit: "contain",
                    }}
                    draggable={false}
                    decoding="async"
                    data-testid="hero-desktop-logo-overlay"
                />
                <div
                    aria-hidden="true"
                    className="absolute pointer-events-none"
                    style={WORDMARK_BACKDROP_STYLE}
                    data-testid="hero-desktop-wordmark-backdrop"
                />
                <img
                    src={WORDMARK_OVERLAY}
                    alt="CrLuys Lifestyle — Improve your Potential Living Way!"
                    className="absolute pointer-events-none select-none"
                    style={WORDMARK_STYLE}
                    draggable={false}
                    decoding="async"
                    data-testid="hero-desktop-wordmark-overlay"
                />
                <PaintedNavLinks />
            </div>
        </section>
    );
}
